// Curated FAQ, grounded in the Survantic user documentation. Answers link into
// the relevant guides (#/docs/<slug>). Keep answers short; the docs hold detail.
//
// Bilingual: each item carries English + French. Link hrefs (#/docs/..., #/faq/...)
// are language-independent and identical across languages.

import type { Lang } from "../i18n";

type Localized = Record<Lang, string>;

export interface FaqItem {
  id: string;
  question: Localized;
  /** Markdown answer. */
  answer: Localized;
  keywords: Localized;
}

/** A FAQ item resolved to a single language, for rendering. */
export interface ResolvedFaqItem {
  id: string;
  question: string;
  answer: string;
  keywords: string;
}

export const FAQ: FaqItem[] = [
  {
    id: "missing-menu",
    question: {
      en: "A menu item or button isn’t showing — why?",
      fr: "Un élément de menu ou un bouton n’apparaît pas — pourquoi?",
    },
    answer: {
      en: "Survantic uses role- and license-based access. If a page or button isn’t visible, your **role** (operator → manager → admin) or your **license module** (e.g. LPR, PPE, GPS) usually doesn’t include it. Ask an admin to adjust your role or check the license tier. See [Overview & roles](#/docs/getting-started/overview).",
      fr: "Survantic gère l’accès selon le rôle et la licence. Si une page ou un bouton n’est pas visible, c’est généralement que votre **rôle** (opérateur → gestionnaire → administrateur) ou votre **module de licence** (p. ex. LPR, PPE, GPS) ne l’inclut pas. Demandez à un administrateur d’ajuster votre rôle ou de vérifier le niveau de licence. Voir [Aperçu et rôles](#/docs/getting-started/overview).",
    },
    keywords: {
      en: "missing menu button hidden permission role license can't see access",
      fr: "manquant menu bouton caché permission rôle licence accès invisible voir",
    },
  },
  {
    id: "roles",
    question: {
      en: "What’s the difference between Operator, Manager, and Admin?",
      fr: "Quelle est la différence entre Opérateur, Gestionnaire et Administrateur?",
    },
    answer: {
      en: "**Operator** — live monitoring, playback, search, events, alerts, day-to-day work. **Manager** — everything an operator can do plus most configuration and edge devices. **Admin** — full access including users, license, failover, integrations, and branding. See [Users, Roles & Permissions](#/docs/admin-guide/setup/users-roles-permissions).",
      fr: "**Opérateur** — surveillance en direct, lecture, recherche, événements, alertes, travail quotidien. **Gestionnaire** — tout ce que peut faire un opérateur, plus la plupart de la configuration et les appareils edge. **Administrateur** — accès complet, y compris les utilisateurs, la licence, la bascule (failover), les intégrations et l’image de marque. Voir [Utilisateurs, rôles et permissions](#/docs/admin-guide/setup/users-roles-permissions).",
    },
    keywords: {
      en: "role operator manager admin permission difference access level",
      fr: "rôle opérateur gestionnaire administrateur permission différence niveau accès",
    },
  },
  {
    id: "add-camera",
    question: {
      en: "How do I add a camera?",
      fr: "Comment ajouter une caméra?",
    },
    answer: {
      en: "Go to the Cameras config and add the camera by IP (or run discovery to find cameras on the network), then enter its credentials. See [Adding Cameras](#/docs/admin-guide/cameras/adding-cameras) and [Camera Discovery](#/docs/admin-guide/cameras/camera-discovery).",
      fr: "Allez dans la configuration des Caméras et ajoutez la caméra par son adresse IP (ou lancez la découverte pour trouver les caméras sur le réseau), puis saisissez ses identifiants. Voir [Ajouter des caméras](#/docs/admin-guide/cameras/adding-cameras) et [Découverte de caméras](#/docs/admin-guide/cameras/camera-discovery).",
    },
    keywords: {
      en: "add camera new onvif rtsp discovery connect",
      fr: "ajouter caméra nouvelle onvif rtsp découverte connecter brancher",
    },
  },
  {
    id: "no-signal",
    question: {
      en: "A camera shows “No Signal” or is stuck on “Connecting…”",
      fr: "Une caméra affiche « Aucun signal » ou reste bloquée sur « Connexion… »",
    },
    answer: {
      en: "Usually the camera is offline, disabled, or the stream isn’t ready. Confirm the camera is **enabled** and reachable, re-check its credentials, and try its sub-stream. Then run the [Troubleshooter](#/docs/admin-guide/tools/troubleshooter). More tips in [Grid View](#/docs/operator-guide/monitoring/grid-view).",
      fr: "Habituellement, la caméra est hors ligne, désactivée, ou le flux n’est pas prêt. Vérifiez que la caméra est **activée** et accessible, revérifiez ses identifiants et essayez son sous-flux. Lancez ensuite l’[Outil de dépannage](#/docs/admin-guide/tools/troubleshooter). Plus de conseils dans la [Vue en grille](#/docs/operator-guide/monitoring/grid-view).",
    },
    keywords: {
      en: "no signal connecting offline black tile not working stream down",
      fr: "aucun signal connexion hors ligne tuile noire ne fonctionne pas flux interrompu",
    },
  },
  {
    id: "find-event",
    question: {
      en: "How do I find a specific event, person, or vehicle?",
      fr: "Comment trouver un événement, une personne ou un véhicule précis?",
    },
    answer: {
      en: "Use [Smart Search](#/docs/operator-guide/investigate/smart-search) to search by what you’re looking for, or browse [Events](#/docs/operator-guide/investigate/events). To follow movement across cameras, use [Journey Tracking](#/docs/operator-guide/investigate/journey-tracking).",
      fr: "Utilisez la [Recherche intelligente](#/docs/operator-guide/investigate/smart-search) pour chercher selon ce que vous voulez trouver, ou parcourez les [Événements](#/docs/operator-guide/investigate/events). Pour suivre un déplacement d’une caméra à l’autre, utilisez le [Suivi de trajet](#/docs/operator-guide/investigate/journey-tracking).",
    },
    keywords: {
      en: "search find event person vehicle investigate footage",
      fr: "rechercher trouver événement personne véhicule enquête images séquence",
    },
  },
  {
    id: "review-recording",
    question: {
      en: "How do I review recorded video?",
      fr: "Comment consulter une vidéo enregistrée?",
    },
    answer: {
      en: "Open [Playback](#/docs/operator-guide/monitoring/playback) to scrub a camera’s timeline, or use the [Recording Explorer](#/docs/operator-guide/investigate/recording-explorer). To keep a clip so it isn’t aged out, see [Saved Clips & Bookmarks](#/docs/operator-guide/investigate/saved-clips-bookmarks) and [Protected Videos](#/docs/operator-guide/investigate/protected-videos).",
      fr: "Ouvrez la [Lecture](#/docs/operator-guide/monitoring/playback) pour parcourir la chronologie d’une caméra, ou utilisez l’[Explorateur d’enregistrements](#/docs/operator-guide/investigate/recording-explorer). Pour conserver un extrait afin qu’il ne soit pas supprimé par la rétention, voir [Extraits sauvegardés et signets](#/docs/operator-guide/investigate/saved-clips-bookmarks) et [Vidéos protégées](#/docs/operator-guide/investigate/protected-videos).",
    },
    keywords: {
      en: "playback recorded video review timeline history footage recording",
      fr: "lecture vidéo enregistrée consulter chronologie historique images enregistrement",
    },
  },
  {
    id: "retention",
    question: {
      en: "How long is video kept, and how do I change it?",
      fr: "Combien de temps les vidéos sont-elles conservées, et comment changer ce délai?",
    },
    answer: {
      en: "Recording and retention are configured per camera. An admin sets continuous and/or event recording and the retention window to match storage. See [Retention](#/docs/admin-guide/cameras/retention).",
      fr: "L’enregistrement et la rétention se configurent par caméra. Un administrateur définit l’enregistrement continu et/ou sur événement ainsi que la fenêtre de rétention selon l’espace de stockage. Voir [Rétention](#/docs/admin-guide/cameras/retention).",
    },
    keywords: {
      en: "retention storage how long keep delete recording days",
      fr: "rétention stockage durée conserver supprimer enregistrement jours",
    },
  },
  {
    id: "users-access",
    question: {
      en: "How do I add users or change what they can access?",
      fr: "Comment ajouter des utilisateurs ou modifier ce à quoi ils ont accès?",
    },
    answer: {
      en: "Admins manage accounts under [Users, Roles & Permissions](#/docs/admin-guide/setup/users-roles-permissions). For single sign-on, see [SAML SSO](#/docs/admin-guide/integrations/saml-sso); for directory sync, [Identity Sync (SCIM)](#/docs/admin-guide/access-control-config/identity-sync-scim).",
      fr: "Les administrateurs gèrent les comptes dans [Utilisateurs, rôles et permissions](#/docs/admin-guide/setup/users-roles-permissions). Pour l’authentification unique, voir [SSO SAML](#/docs/admin-guide/integrations/saml-sso); pour la synchronisation d’annuaire, [Synchronisation des identités (SCIM)](#/docs/admin-guide/access-control-config/identity-sync-scim).",
    },
    keywords: {
      en: "user add account permission role sso saml access manage",
      fr: "utilisateur ajouter compte permission rôle sso saml accès gérer",
    },
  },
  {
    id: "alerts",
    question: {
      en: "How do I get notified by email or SMS?",
      fr: "Comment recevoir des notifications par courriel ou SMS?",
    },
    answer: {
      en: "Configure delivery under [Notifications (SMTP/SMS)](#/docs/admin-guide/integrations/notifications-smtp-sms), then set up triggers in [Alarms](#/docs/admin-guide/monitoring-config/alarms). Operators handle live alerts in [Active Alerts](#/docs/operator-guide/alerts/active-alerts).",
      fr: "Configurez l’envoi dans [Notifications (SMTP/SMS)](#/docs/admin-guide/integrations/notifications-smtp-sms), puis définissez les déclencheurs dans [Alarmes](#/docs/admin-guide/monitoring-config/alarms). Les opérateurs traitent les alertes en direct dans [Alertes actives](#/docs/operator-guide/alerts/active-alerts).",
    },
    keywords: {
      en: "alert notification email sms notify alarm trigger",
      fr: "alerte notification courriel sms notifier alarme déclencheur",
    },
  },
  {
    id: "downloads",
    question: {
      en: "How do I get installers or the mobile app?",
      fr: "Comment obtenir les programmes d’installation ou l’application mobile?",
    },
    answer: {
      en: "Survantic installers and the mobile app are provided by your Survantic representative. Contact support and we’ll send you a secure download link for your plan and platform. Always verify a file against its published checksum before installing.",
      fr: "Les programmes d’installation Survantic et l’application mobile sont fournis par votre représentant Survantic. Communiquez avec le soutien et nous vous enverrons un lien de téléchargement sécurisé adapté à votre forfait et à votre plateforme. Vérifiez toujours un fichier par rapport à sa somme de contrôle publiée avant l’installation.",
    },
    keywords: {
      en: "download installer mobile app apk edge iso software get link",
      fr: "téléchargement installation application mobile apk edge iso logiciel lien obtenir",
    },
  },
  {
    id: "login",
    question: {
      en: "How do I sign in, and what if I forgot my password?",
      fr: "Comment me connecter, et que faire si j’ai oublié mon mot de passe?",
    },
    answer: {
      en: "Sign in with the same email and password as your Survantic tenant — any account under your organization works. If you’re locked out or forgot your password, contact your organization’s admin to reset it. See [Logging in](#/docs/getting-started/logging-in).",
      fr: "Connectez-vous avec le même courriel et mot de passe que votre organisation Survantic — n’importe quel compte de votre organisation fonctionne. Si vous êtes verrouillé ou avez oublié votre mot de passe, communiquez avec l’administrateur de votre organisation pour le réinitialiser. Voir [Connexion](#/docs/getting-started/logging-in).",
    },
    keywords: {
      en: "login sign in password forgot reset locked out credentials",
      fr: "connexion se connecter mot de passe oublié réinitialiser verrouillé identifiants",
    },
  },
  {
    id: "edge",
    question: {
      en: "What is an edge device and when do I need one?",
      fr: "Qu’est-ce qu’un appareil edge et quand en ai-je besoin?",
    },
    answer: {
      en: "An edge appliance records and runs AI analytics on-site, so the system keeps working even if the link to the cloud drops. Admins manage them under [Edge Devices](#/docs/admin-guide/integrations/edge-devices).",
      fr: "Un appareil edge enregistre et exécute l’analyse par IA sur place, de sorte que le système continue de fonctionner même si le lien vers l’infonuagique est interrompu. Les administrateurs les gèrent dans [Appareils edge](#/docs/admin-guide/integrations/edge-devices).",
    },
    keywords: {
      en: "edge device appliance on-site offline local recording analytics",
      fr: "edge appareil sur place hors ligne local enregistrement analyse infonuagique",
    },
  },
];

function resolve(item: FaqItem, lang: Lang): ResolvedFaqItem {
  return {
    id: item.id,
    question: item.question[lang],
    answer: item.answer[lang],
    keywords: item.keywords[lang],
  };
}

export function getFaqList(lang: Lang): ResolvedFaqItem[] {
  return FAQ.map((f) => resolve(f, lang));
}

export function getFaq(lang: Lang, id: string): ResolvedFaqItem | null {
  const item = FAQ.find((f) => f.id === id);
  return item ? resolve(item, lang) : null;
}
